import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import TextField from "@material-ui/core/TextField";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import { getContactDisplayName } from "../../helpers/contactDisplayName";
import api from "../../services/api";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: `min(${drawerWidth}px, 100vw)`,
		flexShrink: 0,
	},
	drawerPaper: {
		width: `min(${drawerWidth}px, 100vw)`,
		backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#f7f9fc",
		border: 0,
		borderLeft: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
		display: "flex",
	},
	header: {
		alignItems: "center",
		backgroundColor: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e7edf5"}`,
		display: "flex",
		minHeight: 72,
		padding: theme.spacing(0, 1.5),
	},
	headerTitle: {
		fontSize: 15,
		fontWeight: 800,
		marginLeft: theme.spacing(0.5),
	},
	content: {
		backgroundColor: theme.palette.type === "dark" ? theme.palette.background.default : "#f7f9fc",
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.25),
		height: "100%",
		padding: theme.spacing(1.5),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		height: 80,
		marginBottom: theme.spacing(1),
		width: 80,
	},

	contactHeader: {
		alignItems: "center",
		border: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e2e8f0"}`,
		borderRadius: 8,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		padding: theme.spacing(2),
		"& > *": {
			marginBottom: theme.spacing(0.5),
		},
	},
	contactName: {
		fontSize: 16,
		fontWeight: 800,
		textAlign: "center",
	},
	contactNumber: {
		color: theme.palette.type === "dark" ? "#bfccff" : "#3154b5",
		fontSize: 13,
	},
	editButton: {
		borderRadius: 8,
		boxShadow: "none",
		fontWeight: 700,
		marginTop: theme.spacing(1),
	},

	contactDetails: {
		border: `1px solid ${theme.palette.type === "dark" ? "#263247" : "#e2e8f0"}`,
		borderRadius: 8,
		padding: theme.spacing(1.5),
		display: "flex",
		flexDirection: "column",
	},
	detailsTitle: {
		fontSize: 12,
		fontWeight: 800,
		marginBottom: theme.spacing(1),
		textTransform: "uppercase",
	},
	detailsHeader: {
		alignItems: "center",
		display: "flex",
		justifyContent: "space-between",
	},
	contactExtraInfo: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#f7f9fc",
		border: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#e7edf5"}`,
		borderRadius: 6,
		marginTop: theme.spacing(0.75),
		padding: theme.spacing(1),
	},
	gnsOsItem: {
		borderTop: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#e7edf5"}`,
		fontSize: 13,
		padding: theme.spacing(1, 0),
	},
	clientLinkHint: {
		color: theme.palette.type === "dark" ? "#a8b8d2" : "#58709b",
		fontSize: 12,
		lineHeight: 1.4,
		marginTop: theme.spacing(0.75),
	},
	clientLinkActions: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1.25),
	},
}));

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
	const displayName = getContactDisplayName(contact);

	const [modalOpen, setModalOpen] = useState(false);
	const [gnsOsLinks, setGnsOsLinks] = useState({ support: null, visits: [] });
	const [gnsOsClientLink, setGnsOsClientLink] = useState(null);
	const [gnsOsClients, setGnsOsClients] = useState([]);
	const [selectedGnsOsClient, setSelectedGnsOsClient] = useState(null);
	const [loadingClientLink, setLoadingClientLink] = useState(false);
	const [savingClientLink, setSavingClientLink] = useState(false);
	const [internalGroupLink, setInternalGroupLink] = useState(null);
	const [internalGroups, setInternalGroups] = useState([]);
	const [selectedInternalGroup, setSelectedInternalGroup] = useState(null);
	const [newInternalGroupName, setNewInternalGroupName] = useState("");
	const [savingInternalGroup, setSavingInternalGroup] = useState(false);
	const emitLocalContactUpdate = changes => {
		window.dispatchEvent(new CustomEvent("whaticket:contact-updated", {
			detail: { ...contact, ...changes }
		}));
	};
	const refreshInternalGroups = async () => {
		try {
			const { data } = await api.get("/contact-internal-groups");
			const groups = data?.groups || [];
			setInternalGroups(groups);
			setSelectedInternalGroup(current => current
				? groups.find(group => group.id === current.id) || current
				: current);
			return groups;
		} catch (err) {
			return [];
		}
	};

	useEffect(() => {
		if (!open || !ticket?.id || !contact?.id) return;
		let active = true;
		setLoadingClientLink(true);
		Promise.all([
			api.get(`/tickets/${ticket.id}/gns-os-support-ticket`).catch(() => ({ data: null })),
			api.get(`/tickets/${ticket.id}/gns-os-visit-requests`).catch(() => ({ data: [] })),
			api.get(`/contacts/${contact.id}/gns-os-client`).catch(() => ({ data: { link: null } })),
			api.get("/tickets/gns-os/clients").catch(() => ({ data: { clients: [] } })),
		]).then(([support, visits, clientLink, clients]) => {
			if (active) {
				const link = clientLink.data?.link || null;
				const availableClients = clients.data?.clients || [];
				setGnsOsLinks({ support: support.data || null, visits: Array.isArray(visits.data) ? visits.data : [] });
				setGnsOsClientLink(link);
				setGnsOsClients(availableClients);
				setSelectedGnsOsClient(link ? availableClients.find(item => item.id === link.gnsOsClientId) || { id: link.gnsOsClientId, nombre: link.gnsOsClientName } : null);
				setLoadingClientLink(false);
			}
		});
		Promise.all([
			api.get(`/contacts/${contact.id}/internal-group`).catch(() => ({ data: { link: null } })),
			refreshInternalGroups()
		]).then(([internalLink, groups]) => {
			if (!active) return;
			const groupLink = internalLink.data?.link || null;
			setInternalGroupLink(groupLink);
			setSelectedInternalGroup(groupLink ? groups.find(item => item.id === groupLink.internalGroupId) || groupLink.internalGroup : null);
		});
		return () => { active = false; };
	}, [open, ticket?.id, contact?.id]);

	const saveGnsOsClientLink = async (authorizeWhatsapp = false) => {
		if (!selectedGnsOsClient) return;
		setSavingClientLink(true);
		try {
			const { data } = await api.put(`/contacts/${contact.id}/gns-os-client`, {
				gnsOsClientId: selectedGnsOsClient.id,
				authorizeWhatsapp,
			});
			setGnsOsClientLink(data.link);
			toast.success(authorizeWhatsapp
				? "Contacto vinculado y autorizado para WhatsApp en GNS-OS"
				: "Contacto vinculado al cliente de GNS-OS");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingClientLink(false);
		}
	};

	const unlinkGnsOsClient = async () => {
		setSavingClientLink(true);
		try {
			await api.delete(`/contacts/${contact.id}/gns-os-client`);
			setGnsOsClientLink(null);
			setSelectedGnsOsClient(null);
			toast.success("Contacto quitado del grupo de cliente");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingClientLink(false);
		}
	};

	const saveInternalGroupLink = async (group = selectedInternalGroup) => {
		if (!group) return;
		setSavingInternalGroup(true);
		try {
			const { data } = await api.put(`/contacts/${contact.id}/internal-group`, { internalGroupId: group.id });
			setInternalGroupLink(data.link);
			setSelectedInternalGroup(data.link?.internalGroup || group);
			emitLocalContactUpdate({ internalGroupLink: data.link });
			toast.success("Contacto agregado al grupo interno");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingInternalGroup(false);
		}
	};

	const createAndAssignInternalGroup = async () => {
		const name = newInternalGroupName.trim();
		if (name.length < 2) {
			toast.error("Escribí un nombre de al menos 2 caracteres");
			return;
		}
		setSavingInternalGroup(true);
		try {
			const { data } = await api.post(`/contacts/${contact.id}/internal-group`, { name });
			const group = data.group;
			setInternalGroups(current => current.some(item => item.id === group.id)
				? current
				: [...current, group].sort((a, b) => a.name.localeCompare(b.name)));
			setInternalGroupLink(data.link);
			setSelectedInternalGroup(data.link?.internalGroup || group);
			emitLocalContactUpdate({ internalGroupLink: data.link });
			setNewInternalGroupName("");
			toast.success("Grupo interno creado y contacto agregado");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingInternalGroup(false);
		}
	};

	const unlinkInternalGroup = async () => {
		setSavingInternalGroup(true);
		try {
			await api.delete(`/contacts/${contact.id}/internal-group`);
			setInternalGroupLink(null);
			setSelectedInternalGroup(null);
			emitLocalContactUpdate({ internalGroupLink: null });
			toast.success("Contacto quitado del grupo interno");
		} catch (err) {
			toastError(err);
		} finally {
			setSavingInternalGroup(false);
		}
	};

	const deleteInternalGroup = async () => {
		const group = selectedInternalGroup || internalGroupLink?.internalGroup;
		if (!group) return;
		if (!window.confirm(`Eliminar el grupo "${group.name}"? Los contactos quedaran sin grupo interno.`)) return;

		setSavingInternalGroup(true);
		try {
			const { data } = await api.delete(`/contact-internal-groups/${group.id}`);
			setInternalGroups(current => current.filter(item => item.id !== group.id));
			if (internalGroupLink?.internalGroupId === group.id) {
				setInternalGroupLink(null);
				emitLocalContactUpdate({ internalGroupLink: null });
			}
			setSelectedInternalGroup(null);
			toast.success(`Grupo eliminado. ${data.unlinkedContacts || 0} contacto(s) desvinculado(s).`);
		} catch (err) {
			toastError(err);
		} finally {
			setSavingInternalGroup(false);
		}
	};

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton aria-label="Cerrar información del contacto" onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography className={classes.headerTitle}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper elevation={0} className={classes.contactHeader}>
						<Avatar
							alt={displayName}
							src={contact.profilePicUrl}
							className={classes.contactAvatar}
						></Avatar>

						<Typography className={classes.contactName}>{displayName}</Typography>
						<Typography className={classes.contactNumber}>
							<Link href={`tel:${contact.number}`}>{contact.number}</Link>
						</Typography>
						<Button
							className={classes.editButton}
							color="primary"
							onClick={() => setModalOpen(true)}
							variant="contained"
						>
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</Paper>
					<Paper elevation={0} className={classes.contactDetails}>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						></ContactModal>
						<Typography className={classes.detailsTitle}>
							{i18n.t("contactDrawer.extraInfo")}
						</Typography>
						{contact?.extraInfo?.map(info => (
							<Paper
								key={info.id}
								square
								variant="outlined"
								className={classes.contactExtraInfo}
							>
								<InputLabel>{info.name}</InputLabel>
								<Typography component="div" noWrap style={{ paddingTop: 2 }}>
									<MarkdownWrapper>{info.value}</MarkdownWrapper>
								</Typography>
							</Paper>
						))}
					</Paper>
					<Paper elevation={0} className={classes.contactDetails}>
						<Typography className={classes.detailsTitle}>Grupo de cliente</Typography>
						{loadingClientLink ? <CircularProgress size={20} /> : (
							<>
								<Autocomplete
									options={gnsOsClients}
									value={selectedGnsOsClient}
									onChange={(_event, client) => setSelectedGnsOsClient(client)}
									getOptionLabel={(option) => option.nombre || ""}
									renderInput={(params) => <TextField {...params} label="Cliente de GNS-OS" variant="outlined" size="small" />}
								/>
								<Typography className={classes.clientLinkHint}>
									{gnsOsClientLink
										? `Vinculacion ${gnsOsClientLink.source === "verified" ? "verificada" : "manual"}.`
										: "Este contacto aparecera en Sin vincular hasta elegir un cliente."}
								</Typography>
								<div className={classes.clientLinkActions}>
									<Button color="primary" disabled={!selectedGnsOsClient || savingClientLink} onClick={() => saveGnsOsClientLink(false)} variant="contained">
										{gnsOsClientLink ? "Actualizar" : "Vincular"}
									</Button>
									<Button color="secondary" disabled={!selectedGnsOsClient || savingClientLink} onClick={() => saveGnsOsClientLink(true)} variant="outlined">
										{gnsOsClientLink ? "Autorizar WhatsApp" : "Vincular y autorizar"}
									</Button>
									{gnsOsClientLink && <Button color="default" disabled={savingClientLink} onClick={unlinkGnsOsClient}>Quitar</Button>}
								</div>
							</>
						)}
					</Paper>
					<Paper elevation={0} className={classes.contactDetails}>
						<div className={classes.detailsHeader}>
							<Typography className={classes.detailsTitle}>Grupo interno</Typography>
							<Tooltip title="Eliminar grupo seleccionado">
								<span>
									<IconButton aria-label="Eliminar grupo interno" disabled={!(selectedInternalGroup || internalGroupLink?.internalGroup) || savingInternalGroup} onClick={deleteInternalGroup} size="small">
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</div>
						{loadingClientLink ? <CircularProgress size={20} /> : (
							<>
								<TextField
									fullWidth
									label="Grupo interno de Whaticket"
									InputLabelProps={{ shrink: true }}
									margin="dense"
									onFocus={refreshInternalGroups}
									onChange={(event) => setSelectedInternalGroup(internalGroups.find(group => String(group.id) === event.target.value) || null)}
									SelectProps={{ native: true, displayEmpty: true }}
									select
									value={selectedInternalGroup ? String(selectedInternalGroup.id) : ""}
									variant="outlined"
									size="small"
								>
									<option disabled value="">Seleccionar grupo interno</option>
									{internalGroups.map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
								</TextField>
								<Typography className={classes.clientLinkHint}>
									{internalGroupLink ? `Grupo actual: ${internalGroupLink.internalGroup?.name || "Sin nombre"}.` : "Este grupo se guarda solo en Whaticket."}
								</Typography>
								<TextField
									fullWidth
									label="Nuevo grupo interno"
									margin="dense"
									onChange={(event) => setNewInternalGroupName(event.target.value)}
									value={newInternalGroupName}
									variant="outlined"
									size="small"
								/>
								<div className={classes.clientLinkActions}>
									<Button color="primary" disabled={!selectedInternalGroup || savingInternalGroup} onClick={() => saveInternalGroupLink()} variant="contained">
										{internalGroupLink ? "Actualizar grupo" : "Agregar al grupo"}
									</Button>
									<Button color="secondary" disabled={newInternalGroupName.trim().length < 2 || savingInternalGroup} onClick={createAndAssignInternalGroup} variant="outlined">
										Crear y agregar
									</Button>
									{internalGroupLink && <Button color="default" disabled={savingInternalGroup} onClick={unlinkInternalGroup}>Quitar</Button>}
								</div>
							</>
						)}
					</Paper>
					{(gnsOsLinks.support || gnsOsLinks.visits.length > 0) && (
						<Paper elevation={0} className={classes.contactDetails}>
							<Typography className={classes.detailsTitle}>Gestiones GNS-OS</Typography>
							{gnsOsLinks.support && <Typography className={classes.gnsOsItem}>Ticket de soporte #{gnsOsLinks.support.gnsOsTicketId || gnsOsLinks.support.id}</Typography>}
							{gnsOsLinks.visits.map(visit => <Typography className={classes.gnsOsItem} key={visit.id}>Solicitud de visita #{visit.gnsOsVisitRequestId || visit.id}</Typography>)}
						</Paper>
					)}
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
